/**
 * 服务端地址的解析。只有两个纯函数，但它们决定了「手机能不能连上你笔记本上的 dev server」。
 *
 * 背景：`GET /api/config` 下发的 `wsUrl` 是**服务端自己**的地址（默认
 * `ws://localhost:8787/ws`）。这个值对「浏览器和服务端在同一台机器上」成立，
 * 对下面两种真实情况都不成立：
 *
 *   1. 手机通过局域网访问 `http://192.168.1.5:5173` —— 手机上的 `localhost` 是它自己，
 *      照抄下发地址等于让手机连它自己的 8787 端口；
 *   2. 页面是 https（内网穿透 / 正式部署），而下发地址是 ws:// —— 浏览器直接按
 *      混合内容拦掉，控制台里连个像样的报错都没有。
 *
 * 所以规则是：**下发地址指向回环且与页面不同源时，改走页面同源的那条路径**
 * （dev 下 vite 的 `/ws` 代理会把它转回服务端；生产下同源反代同理）。
 * 其余情况一律照抄下发值 —— 多区域重定向就是靠它，绝不能在客户端硬编码。
 */

/** 回环主机名。`::1` 会被 URL 解析成带方括号的形式，两种写法都收。 */
const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]', '::1', '0.0.0.0'])

export function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK.has(hostname.toLowerCase())
}

/** http(s) → ws(s)。页面是 https 时必须用 wss，否则被混合内容策略拦掉。 */
export function wsSchemeFor(pageProtocol: string): 'ws:' | 'wss:' {
  return pageProtocol === 'https:' ? 'wss:' : 'ws:'
}

/**
 * 把 `/api/config` 下发的 wsUrl 归一到「这个页面真的连得上」的地址。
 *
 * @param configured 服务端下发的 wsUrl
 * @param pageOrigin 当前页面的 origin（`location.origin`）
 */
export function resolveWsUrl(configured: string, pageOrigin: string): string {
  let page: URL
  try {
    page = new URL(pageOrigin)
  } catch {
    // 没有可用的页面 origin（SSR / 测试）：原样返回，让调用方自己决定
    return configured
  }
  const sameOriginWs = (path: string): string => `${wsSchemeFor(page.protocol)}//${page.host}${path}`

  let target: URL
  try {
    target = new URL(configured)
  } catch {
    // 下发的是相对路径（'/ws'）或干脆是坏值：退回同源
    return sameOriginWs(configured.startsWith('/') ? configured : '/ws')
  }

  // 页面是 https 而下发是明文 ws：走同源，让反代去做 TLS 终止
  if (page.protocol === 'https:' && target.protocol === 'ws:') return sameOriginWs(target.pathname)

  // 下发指向回环，但页面不是从同一个 host:port 打开的 —— 手机 / 另一个 origin 都属于这种
  if (isLoopbackHost(target.hostname) && target.host !== page.host) return sameOriginWs(target.pathname)

  return configured
}

/** HTTP API 的基址。同源即可（dev 下 vite 代理 `/api`），拿不到 location 时退回 dev 默认值。 */
export function apiBaseUrl(): string {
  const origin = (globalThis as { location?: { origin?: string } }).location?.origin
  return origin ?? 'http://localhost:5173'
}
